import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useOrganization } from '@clerk/clerk-react';
import {
  Building2,
  TrendingUp,
  Users,
  MousePointerClick,
  ShieldCheck,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Camera,
  Trash2,
  Sparkles,
  Link2,
  Check,
  X,
  History,
} from 'lucide-react';
import { useAuthContext } from '@/hooks/useAuthContext';
import { usePartnerOverview } from '@/hooks/usePartnerOverview';
import { api } from '@/lib/api-client';
import { validateReferralCodeInBrowser } from '@/lib/referral-validator';

export const SettingsOrganizationCard: React.FC = () => {
  const { organization } = useOrganization();
  const { orgName, orgRole, orgTier, orgStatus } = useAuthContext();
  const { data: overview, isLoading: isOverviewLoading, refetch: refetchOverview } = usePartnerOverview();

  const [companyName, setCompanyName] = useState(organization?.name || orgName || '');
  const [customReferralCode, setCustomReferralCode] = useState(overview?.referralCode || '');
  const [isUpdating, setIsUpdating] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const canManage = orgRole === 'owner' || orgRole === 'admin' || orgRole === 'org:admin';

  useEffect(() => {
    if (organization?.name) {
      setCompanyName(organization.name);
    }
  }, [organization?.name]);

  useEffect(() => {
    if (overview?.referralCode) {
      setCustomReferralCode(overview.referralCode);
    }
  }, [overview?.referralCode]);

  // Real-time In-Browser Client-side Validation (zero server traffic)
  const validation = useMemo(() => {
    return validateReferralCodeInBrowser(customReferralCode);
  }, [customReferralCode]);

  const hasCodeChanged = useMemo(() => {
    return validation.normalizedCode !== (overview?.referralCode || '').toUpperCase();
  }, [validation.normalizedCode, overview?.referralCode]);

  const hasNameChanged = useMemo(() => {
    return companyName.trim() !== (organization?.name || orgName || '').trim();
  }, [companyName, organization?.name, orgName]);

  const canSubmit = canManage && !isUpdating && (!hasCodeChanged || validation.isValid);

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!companyName.trim()) {
      setError('Organization legal name is required.');
      return;
    }

    if (hasCodeChanged && !validation.isValid) {
      setError(validation.errorMessage || 'Please enter a valid referral code.');
      return;
    }

    setIsUpdating(true);
    setError(null);
    setSuccess(null);

    try {
      // 1. Update Organization legal name in Clerk if changed
      if (organization && hasNameChanged) {
        await organization.update({ name: companyName.trim() });
      }

      // 2. Update Custom Referral Code if changed
      if (hasCodeChanged) {
        const res = await api.put<{
          success: boolean;
          data: { code: string; referralUrl: string };
          error?: { message: string };
        }>('/api/v1/partner/referral-code', {
          code: validation.normalizedCode,
          orgName: companyName.trim(),
        });

        if (!res?.success) {
          throw new Error(res?.error?.message || 'Failed to update custom referral code.');
        }
      }

      // 3. Refresh overview stats
      await refetchOverview();

      setSuccess('Organization profile and referral code saved successfully.');
      setTimeout(() => setSuccess(null), 3500);
    } catch (err: any) {
      setError(
        err?.data?.error?.message ||
        err?.errors?.[0]?.message ||
        err?.message ||
        'Failed to update organization profile.'
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !organization) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file (PNG, JPG, SVG, WebP).');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('Image file size must be under 10MB.');
      return;
    }

    setIsUploadingLogo(true);
    setError(null);
    setSuccess(null);

    try {
      await organization.setLogo({ file });
      await organization.reload?.();
      setSuccess('Organization logo uploaded and saved to Clerk successfully.');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err?.errors?.[0]?.message || err?.message || 'Failed to upload organization logo.');
    } finally {
      setIsUploadingLogo(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveLogo = async () => {
    if (!organization) return;
    setIsUploadingLogo(true);
    setError(null);
    setSuccess(null);

    try {
      await organization.setLogo({ file: null });
      await organization.reload?.();
      setSuccess('Organization logo removed successfully.');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err?.errors?.[0]?.message || err?.message || 'Failed to remove organization logo.');
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const activeName = organization?.name || orgName || 'My Organization';
  const orgInitials = activeName
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const previewCode = validation.normalizedCode || 'PARTNER';
  const previewUrl = `https://conversora.io/r/${previewCode.toLowerCase()}`;

  return (
    <div className="bg-[#0E1015] border border-zinc-800 rounded-2xl p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between pb-4 border-b border-zinc-800">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-indigo-600/10 border border-indigo-500/20 flex items-center justify-center text-indigo-400">
            <Building2 className="w-4 h-4" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-white">Organization Profile & Performance</h2>
            <p className="text-xs text-zinc-400">
              Manage your partner entity credentials and customize referral identifiers
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-[10px] font-semibold uppercase px-2 py-0.5 bg-indigo-500/15 text-indigo-400 border border-indigo-500/30 rounded-md">
            {orgTier || 'Referral'} Partner
          </span>
          <span className="text-[10px] px-2 py-0.5 bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 rounded-md capitalize">
            {orgStatus || 'Active'}
          </span>
        </div>
      </div>

      {/* Organization Logo & Avatar Upload */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 rounded-xl bg-zinc-950 border border-zinc-850">
        <div className="flex items-center gap-4">
          <div className="relative group">
            {organization?.imageUrl ? (
              <img
                src={organization.imageUrl}
                alt={activeName}
                className="w-16 h-16 rounded-2xl object-cover ring-2 ring-zinc-700/60 shadow-md"
              />
            ) : (
              <div className="w-16 h-16 rounded-2xl bg-linear-to-tr from-indigo-600 to-violet-500 flex items-center justify-center font-bold text-white text-lg shadow-md">
                {orgInitials}
              </div>
            )}
            {isUploadingLogo && (
              <div className="absolute inset-0 bg-black/60 rounded-2xl flex items-center justify-center">
                <Loader2 className="w-5 h-5 text-indigo-400 animate-spin" />
              </div>
            )}
          </div>

          <div>
            <p className="text-sm font-semibold text-white">{activeName}</p>
            <p className="text-xs text-zinc-400 mt-0.5">PNG, JPG, SVG or WebP. Max 10MB.</p>
          </div>
        </div>

        {canManage && (
          <div className="flex items-center gap-2 self-start sm:self-auto">
            <input
              type="file"
              ref={fileInputRef}
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              className="hidden"
              onChange={handleLogoUpload}
            />
            <button
              type="button"
              disabled={isUploadingLogo}
              onClick={() => fileInputRef.current?.click()}
              className="px-3.5 py-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white rounded-xl text-xs font-semibold transition cursor-pointer flex items-center gap-2 shadow-xs"
            >
              <Camera className="w-3.5 h-3.5" />
              <span>{organization?.hasImage ? 'Change Logo' : 'Upload Logo'}</span>
            </button>

            {organization?.hasImage && (
              <button
                type="button"
                disabled={isUploadingLogo}
                onClick={handleRemoveLogo}
                className="px-3 py-2 bg-zinc-900 hover:bg-zinc-800 text-rose-400 hover:text-rose-300 border border-zinc-800 rounded-xl text-xs font-medium transition cursor-pointer flex items-center gap-1.5"
                title="Remove Organization Logo"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span>Remove</span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Organization Insights Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-zinc-950 border border-zinc-850 p-3.5 rounded-xl">
          <div className="flex items-center justify-between text-zinc-400 mb-1">
            <span className="text-[11px] font-medium">Referred Leads</span>
            <Users className="w-3.5 h-3.5 text-indigo-400" />
          </div>
          <p className="text-lg font-bold text-white">
            {isOverviewLoading ? '...' : (overview?.totalLeads ?? 0)}
          </p>
          <p className="text-[10px] text-zinc-400 mt-0.5">Total customer signups</p>
        </div>

        <div className="bg-zinc-950 border border-zinc-850 p-3.5 rounded-xl">
          <div className="flex items-center justify-between text-zinc-400 mb-1">
            <span className="text-[11px] font-medium">Active Subscriptions</span>
            <TrendingUp className="w-3.5 h-3.5 text-emerald-400" />
          </div>
          <p className="text-lg font-bold text-white">
            {isOverviewLoading ? '...' : (overview?.activeSubscriptions ?? 0)}
          </p>
          <p className="text-[10px] text-zinc-400 mt-0.5">Paying clients</p>
        </div>

        <div className="bg-zinc-950 border border-zinc-850 p-3.5 rounded-xl">
          <div className="flex items-center justify-between text-zinc-400 mb-1">
            <span className="text-[11px] font-medium">Link Clicks</span>
            <MousePointerClick className="w-3.5 h-3.5 text-sky-400" />
          </div>
          <p className="text-lg font-bold text-white">
            {isOverviewLoading ? '...' : (overview?.totalClicks ?? 0)}
          </p>
          <p className="text-[10px] text-zinc-400 mt-0.5">Attributed visits</p>
        </div>

        <div className="bg-zinc-950 border border-zinc-850 p-3.5 rounded-xl">
          <div className="flex items-center justify-between text-zinc-400 mb-1">
            <span className="text-[11px] font-medium">Conversion Rate</span>
            <ShieldCheck className="w-3.5 h-3.5 text-violet-400" />
          </div>
          <p className="text-lg font-bold text-white">
            {isOverviewLoading ? '...' : `${overview?.conversionRatePercent ?? 0}%`}
          </p>
          <p className="text-[10px] text-zinc-400 mt-0.5">Click-to-lead ratio</p>
        </div>
      </div>

      {success && (
        <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4" />
          <span>{success}</span>
        </div>
      )}

      {error && (
        <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-400 text-xs flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          <span>{error}</span>
        </div>
      )}

      {/* Edit Form */}
      <form onSubmit={handleUpdateProfile} className="space-y-5 max-w-lg">
        <div>
          <label className="block text-xs font-medium text-zinc-300 mb-1.5">
            Organization / Agency Legal Name
          </label>
          <input
            type="text"
            required
            disabled={!canManage}
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-3.5 py-2.5 text-xs text-white focus:outline-none focus:border-indigo-500 disabled:opacity-50"
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="block text-xs font-medium text-zinc-300">
              Custom Referral Code
            </label>
            <span className="text-[10px] text-zinc-400 font-mono">
              {validation.normalizedCode.length} / 24 chars
            </span>
          </div>

          <div className="relative">
            <input
              type="text"
              required
              disabled={!canManage}
              value={customReferralCode}
              onChange={(e) => setCustomReferralCode(e.target.value)}
              placeholder="e.g. MYAGENCY"
              maxLength={24}
              className={`w-full bg-zinc-950 border rounded-xl px-3.5 py-2.5 text-xs font-mono uppercase text-white focus:outline-none tracking-wider pr-10 transition ${
                !validation.isValid && customReferralCode.trim()
                  ? 'border-rose-500/60 focus:border-rose-500'
                  : validation.isValid && hasCodeChanged
                  ? 'border-emerald-500/60 focus:border-emerald-500'
                  : 'border-zinc-800 focus:border-indigo-500'
              } disabled:opacity-50`}
            />
            <div className="absolute right-3 top-2.5 pointer-events-none">
              {validation.isValid ? (
                <Check className="w-4 h-4 text-emerald-400" />
              ) : customReferralCode.trim() ? (
                <X className="w-4 h-4 text-rose-400" />
              ) : (
                <Sparkles className="w-4 h-4 text-indigo-400" />
              )}
            </div>
          </div>

          {/* Real-time In-Browser Validation Checks (Zero server requests) */}
          <div className="mt-2.5 p-3 rounded-xl bg-zinc-950/80 border border-zinc-850 space-y-2">
            <p className="text-[11px] font-medium text-zinc-300">Format Standards:</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-[10px]">
              <div className="flex items-center gap-1.5">
                <span
                  className={`w-3.5 h-3.5 rounded-full flex items-center justify-center text-[9px] ${
                    validation.ruleStatus.length ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-800 text-zinc-400'
                  }`}
                >
                  {validation.ruleStatus.length ? '✓' : '•'}
                </span>
                <span className={validation.ruleStatus.length ? 'text-zinc-200' : 'text-zinc-400'}>
                  3–24 characters long
                </span>
              </div>

              <div className="flex items-center gap-1.5">
                <span
                  className={`w-3.5 h-3.5 rounded-full flex items-center justify-center text-[9px] ${
                    validation.ruleStatus.characters ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-800 text-zinc-400'
                  }`}
                >
                  {validation.ruleStatus.characters ? '✓' : '•'}
                </span>
                <span className={validation.ruleStatus.characters ? 'text-zinc-200' : 'text-zinc-400'}>
                  Letters, numbers, -, _ only
                </span>
              </div>

              <div className="flex items-center gap-1.5">
                <span
                  className={`w-3.5 h-3.5 rounded-full flex items-center justify-center text-[9px] ${
                    validation.ruleStatus.boundary ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-800 text-zinc-400'
                  }`}
                >
                  {validation.ruleStatus.boundary ? '✓' : '•'}
                </span>
                <span className={validation.ruleStatus.boundary ? 'text-zinc-200' : 'text-zinc-400'}>
                  Starts & ends with letter/number
                </span>
              </div>

              <div className="flex items-center gap-1.5">
                <span
                  className={`w-3.5 h-3.5 rounded-full flex items-center justify-center text-[9px] ${
                    validation.ruleStatus.notReserved ? 'bg-emerald-500/20 text-emerald-400' : 'bg-rose-500/20 text-rose-400'
                  }`}
                >
                  {validation.ruleStatus.notReserved ? '✓' : '✗'}
                </span>
                <span className={validation.ruleStatus.notReserved ? 'text-zinc-200' : 'text-rose-400'}>
                  Not a reserved keyword
                </span>
              </div>
            </div>

            {/* Error Message if any rule fails */}
            {validation.errorMessage && customReferralCode.trim() && (
              <p className="text-[10px] text-rose-400 pt-1 font-medium flex items-center gap-1">
                <AlertCircle className="w-3 h-3 shrink-0" />
                <span>{validation.errorMessage}</span>
              </p>
            )}
          </div>

          <div className="mt-2.5 flex items-center gap-1.5 text-[11px] text-zinc-400 font-mono bg-zinc-950/70 border border-zinc-850 px-3 py-1.5 rounded-lg overflow-x-auto">
            <Link2 className="w-3 h-3 text-zinc-400 shrink-0" />
            <span className="text-zinc-400">Attribution Link:</span>
            <span className="text-indigo-400 select-all">{previewUrl}</span>
          </div>

          {/* Active Legacy Aliases (Zero Link-Rot) */}
          {overview?.aliases && overview.aliases.length > 0 && (
            <div className="mt-2.5 p-3 rounded-xl bg-zinc-950/80 border border-zinc-850 space-y-1.5">
              <div className="flex items-center gap-1.5 text-[11px] font-medium text-zinc-300">
                <History className="w-3.5 h-3.5 text-indigo-400 shrink-0" />
                <span>Active Legacy Aliases (Zero Link-Rot):</span>
              </div>
              <p className="text-[10px] text-zinc-400">
                Previously customized codes remain permanently active and automatically attribute credit to your organization:
              </p>
              <div className="flex flex-wrap gap-1.5 pt-1">
                {overview.aliases.map((alias) => (
                  <span
                    key={alias}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-zinc-900 border border-zinc-800 text-[10px] font-mono text-zinc-300 shadow-xs"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    <span className="font-semibold text-white">{alias}</span>
                    <span className="text-[9px] text-zinc-400 font-sans">↳ Active</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {canManage ? (
          <button
            type="submit"
            disabled={!canSubmit}
            className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white rounded-xl text-xs font-semibold transition cursor-pointer flex items-center gap-2 shadow-xs"
          >
            {isUpdating && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            <span>Save Organization Profile</span>
          </button>
        ) : (
          <p className="text-[11px] text-zinc-400 italic">
            Only workspace owners and administrators can edit organization legal details and customize the referral code.
          </p>
        )}
      </form>
    </div>
  );
};
